import { defineStore } from 'pinia';
import { Router, RouteLocationRaw } from 'vue-router';
import { identifyUser, resetAnalytics, setUserProperties, trackEvent } from '@/services/analytics.service';
// @ts-ignore
import { stytch } from '@/stytch';
import Cookies from 'js-cookie';

interface State {
  isAuthenticated: boolean;
  isLoggedOut: boolean;
  userId: string | null;
  orgId: string | null;
  email: string | null;
  username: string | null;
  isSharingNetworkLocked: boolean;
}

interface TokenBundle {
  access_token: string;
  expires_in: number;
  userId: string;
  orgId: string;
  email: string;
  username: string;
}

export const useAuthStore = defineStore('auth', {
  state: (): State => {
    return {
      isAuthenticated: false,
      isLoggedOut: false,
      userId: null,
      orgId: null,
      email: sessionStorage.getItem('email') || null,
      username: sessionStorage.getItem('username') || null,
      isSharingNetworkLocked: sessionStorage.getItem('isSharingNetworkLocked') === 'true',
    };
  },
  actions: {
    setIsAuthenticated(isAuthenticated: boolean) {
      this.isAuthenticated = isAuthenticated;
    },
    checkAuthStatus(router: Router) {
      const authToken = Cookies.get('auth_token');
      this.isAuthenticated = Boolean(stytch.session.getSync()) || Boolean(authToken); 
      if (!this.isAuthenticated && !this.isLoggedOut && !router.currentRoute.value.path.includes('login')) {
        router.push({ name: 'login' });
        this.logout(router);
      }
      return this.isAuthenticated;
    },
    logout(router: Router) {
      document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
      trackEvent('Logged out');
      resetAnalytics();
      router.push({ name: 'login' });
      try {
        const session = stytch.session.getSync();
        if (session) {
          stytch.session.revoke();
        }
      } catch (error) {
        console.warn('Stytch session revocation failed:', error);
      }
      sessionStorage.clear();
      this.isAuthenticated = false;
      this.isLoggedOut = true;
      this.userId = null;
      this.orgId = null;
      this.email = null;
      this.username = null;
      this.isSharingNetworkLocked = false;
    }, 
    setIsLoggedOut(isLoggedOut: boolean) {
      this.isLoggedOut = isLoggedOut;
    },
    setUserData(userId: string, orgId: string, email: string | null = null, username: string | null = null) {
      this.userId = userId;
      this.orgId = orgId;
      this.email = email;
      this.username = username;
      
      if (email) {  
        sessionStorage.setItem('email', email || '');
      } else {
        sessionStorage.removeItem('email');
      }
      
      if (username) {
        sessionStorage.setItem('username', username || '');
      } else {
        sessionStorage.removeItem('username');
      }
    },
    /**
     * Applies a backend-issued token bundle (see fastapi `_mint_token_bundle`) to the
     * current session: sets the auth cookie, updates analytics identity, updates this
     * store's state, and redirects to the destination route.
     *
     * Shared by password login (LoginView) and the one-time sign-in link
     * (AuthCallbackView) so both flows end up in the exact same authenticated state.
     */
    completeLogin(tokenBundle: TokenBundle, router: Router, redirectPath?: RouteLocationRaw) {
      document.cookie = `auth_token=${tokenBundle.access_token}; Secure; SameSite=None; Max-Age=${tokenBundle.expires_in}; Path=/;`;

      identifyUser(`${tokenBundle.username}::${tokenBundle.userId}`, {
        email: tokenBundle.email,
      });
      setUserProperties({
        $name: tokenBundle.username,
        $email: tokenBundle.email,
        $last_seen: Date(),
        'User ID': tokenBundle.userId,
        'Org ID': tokenBundle.orgId,
      });

      this.setUserData(tokenBundle.userId, tokenBundle.orgId, tokenBundle.email, tokenBundle.username);
      this.setIsLoggedOut(false);

      router.push(redirectPath || { name: 'Project Viewer' });
    },
  },
});
